import { useQueryClient } from '@tanstack/react-query';
import {
  searchTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  exportTemplate,
  deleteTemplate,
  uploadTemplate,
} from '../../api/templates.api';
import { SearchTemplatesApiRequest } from '../../../../types/requests/templates/search-templates.request';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { TemplateApiModel } from '../../../../types/models/template.model';
import { GetTemplateApiRequest } from '../../../../types/requests/templates/get-template.request';
import { CreateTemplateApiRequest } from '../../../../types/requests/templates/create-template.request';
import { UpdateTemplateApiRequest } from '../../../../types/requests/templates/update-template.request';
import { ExportTemplateApiRequest } from '../../../../types/requests/templates/export-template.request';
import { DeleteTemplateApiRequest } from '../../../../types/requests/templates/delete-template.request';

const baseQueryKey = 'template';
const searchQueryKey = 'search';

export const useCreateTemplate = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateTemplateApiRequest) => (await createTemplate(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, template: data }] as const;
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateTemplateApiRequest) => (await updateTemplate(params)).data,
    onSuccess: (template) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === 'search' || (queryKey[1] as GetTemplateApiRequest)?.templateId === template.id),
      }),
  });
  return [request, { ...result, template: data }] as const;
};

export const useTemplates = (params: SearchTemplatesApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchTemplates({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useGetTemplate = <T extends TemplateApiModel>(params: GetTemplateApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getTemplate<T>(params)).data,
    enabled: Boolean(params.templateId && !Number.isNaN(params.templateId)),
  });
  return { ...result, template: data } as const;
};

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteTemplateApiRequest) => (await deleteTemplate(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useUploadTemplate = () => {
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: FormData) => (await uploadTemplate(params)).data,
  });
  return [request, { ...result, filePath: data }] as const;
};

export const useExportTemplate = () => {
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: ExportTemplateApiRequest) => (await exportTemplate(params)).data,
  });
  return [request, { ...result, filePath: data }] as const;
};
